import type { ContactTranslations } from './translations.types'

export const defaultTranslations: ContactTranslations = {
  schema: {
    name: {
      required: 'El nombre es obligatorio',
      maxLength: 'El nombre no puede exceder 200 caracteres',
    },
    email: {
      invalidFormat: 'El formato del correo electrónico no es válido',
      maxLength: 'El correo no puede exceder 256 caracteres',
    },
    address: {
      maxLength: 'La dirección no puede exceder 500 caracteres',
    },
    phone: {
      countryRequired: 'El país es obligatorio',
      areaCodeMaxLength: 'El área no puede exceder 3 caracteres',
      numberRequired: 'El número de teléfono es obligatorio',
      numberMaxLength: 'El número no puede exceder 10 caracteres',
      extensionMaxLength: 'La extensión no puede exceder 3 caracteres',
    },
    socialNetwork: {
      typeRequired: 'Debe seleccionar un tipo de red social',
      urlRequired: 'La URL del perfil es obligatoria',
      urlMaxLength: 'La URL no puede exceder 2048 caracteres',
    },
    useFor: {
      required: 'El contacto debe tener al menos un uso',
    },
  },
  modal: {
    titlePrefix: 'Contacto',
    actionBadge: {
      creating: 'Creando',
      editing: 'Editando',
    },
    description: 'Formulario de contacto',
    button: {
      save: 'Guardar',
      update: 'Actualizar',
      cancel: 'Cancelar',
      close: 'Cerrar',
      addPhone: 'Agregar teléfono',
      addSocialNetwork: 'Agregar red social',
    },
    label: {
      name: 'Nombre',
      email: 'Email',
      address: 'Dirección',
      description: 'Descripción',
      useFor: 'Usar para',
      country: 'País',
      state: 'Estado/Departamento',
      city: 'Ciudad',
      neighborhood: 'Barrio',
      category: 'Categoría',
      phoneNumber: 'Número',
      socialType: 'Red social',
      socialUrl: 'URL',
    },
    placeholder: {
      name: 'Nombre del contacto',
      address: 'Dirección del contacto',
      description: 'Descripción del contacto',
      select: '\u2014 Seleccionar \u2014',
      loading: 'Cargando...',
      socialUrl: 'https://...',
    },
    tab: {
      basicData: 'Datos básicos',
      location: 'Ubicación',
      phone: 'Teléfono',
      socialNetwork: 'Redes sociales',
    },
    section: {
      location: 'Ubicación',
      phones: 'Teléfonos',
      socialNetworks: 'Redes Sociales',
    },
    error: {
      preventRemoval: 'Debe existir al menos un contacto Predeterminado',
      transferBlocked: 'No se puede transferir. El contacto predeterminado actual no tiene otros usos y quedaría sin ninguno.',
      neighborhoodMismatch: 'El barrio seleccionado no pertenece a la ciudad indicada',
      catalogsPhone: 'Error al cargar catálogos. Las opciones de país pueden no estar disponibles.',
      catalogsSocial: 'Error al cargar catálogos. Los tipos de red social pueden no estar disponibles.',
    },
    aria: {
      phoneCategory: (index: number) => `Categoría del teléfono ${index + 1}`,
      phoneCountry: (index: number) => `País del teléfono ${index + 1}`,
      removePhone: (index: number) => `Eliminar teléfono ${index + 1}`,
      socialType: (index: number) => `Tipo de red social ${index + 1}`,
      socialUrl: (index: number) => `URL de red social ${index + 1}`,
      removeSocial: (index: number) => `Eliminar red social ${index + 1}`,
      country: 'País',
      state: 'Estado/Departamento',
      city: 'Ciudad',
      neighborhood: 'Barrio',
    },
    defaultCategory: 'Móvil',
    emptyState: {
      phone: 'No hay registros. Haz clic en "Agregar teléfono"',
      socialNetwork: 'No hay registros. Haz clic en "Agregar red social"',
    },
    column: {
      actions: 'Acciones',
    },
  },
  grid: {
    title: 'Contacto',
    button: { addContact: 'Agregar contacto' },
    aria: { table: 'Tabla de contactos' },
    column: {
      name: 'Nombre',
      email: 'Email',
      useFor: 'Usar para',
      phone: 'Teléfono',
      socialNetworks: 'Redes sociales',
      actions: 'Acciones',
    },
    pagination: {
      previous: 'Anterior',
      next: 'Siguiente',
    },
    emptyBanner: 'No hay registros. Haz clic en \u201CAgregar contacto\u201D',
    socialNetworkNotEnabled: 'Red social no habilitada',
  },
  actions: {
    pendingDelete: 'Pendiente de eliminación',
    undo: 'Deshacer',
    viewDetail: 'Ver Detalle',
    edit: 'Editar',
    delete: 'Eliminar',
  },
  emptyState: {
    noContacts: 'No hay contactos registrados.',
    addHint: " Haz clic en 'Agregar Contacto' para comenzar.",
    addButton: 'Agregar Contacto',
  },
  pillEmail: {
    error: {
      invalidFormat: 'El formato del correo electrónico no es válido',
      duplicate: 'Este correo ya fue agregado',
    },
    placeholder: 'Ingrese correo y presione Enter...',
    aria: {
      list: 'Correos electrónicos',
      emailLabel: (email: string) => `Correo: ${email}`,
      removeEmail: (email: string) => `Eliminar ${email}`,
      addEmail: 'Agregar correo electrónico',
    },
  },
  phoneInput: {
    placeholder: {
      area: 'Área',
      number: 'Número de teléfono',
      extension: 'Ext.',
    },
    aria: {
      group: 'Número de teléfono: área, número, extensión',
      areaCode: 'Código de área',
      number: 'Número',
      extension: 'Extensión',
    },
  },
  tagSelector: {
    selectAll: 'Usar para todo',
    aria: {
      group: 'Usar para',
      option: (label: string) => `Usar para: ${label}`,
    },
  },
  manager: {
    defaultLabel: 'Predeterminado',
  },
  dialog: {
    default: {
      title: 'Cambiar contacto predeterminado',
      newContactFallback: 'el nuevo contacto',
      message: (currentName: string, newName: string) =>
        `El contacto \u201C${currentName}\u201D ya está marcado como Predeterminado. ¿Desea transferir el predeterminado a \u201C${newName}\u201D?`,
      cancel: 'Cancelar',
      confirm: 'Confirmar',
    },
    delete: {
      title: 'Eliminar',
      message: '¿Estás seguro de eliminar el registro?',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
    },
    deleteDefault: {
      title: 'Eliminar contacto predeterminado',
      message: (defaultName: string, newDefaultName: string) =>
        `El contacto \u201C${defaultName}\u201D es el predeterminado. Al eliminarlo, el predeterminado se transferirá a \u201C${newDefaultName}\u201D. ¿Desea continuar?`,
      cancel: 'Cancelar',
      confirm: 'Confirmar',
    },
  },
  skeleton: {
    column: {
      name: 'Nombre',
      email: 'Email',
      phone: 'Teléfono',
      actions: 'Acciones',
    },
  },
  enums: {
    useFor: { 0: 'Predeterminado' },
    phoneCategory: { 0: 'Móvil' },
  },
}
